import { useEffect, useState } from 'react';
import api from 'services/api';
import { setVideoDecrytionKey, addVideoThumbnail } from 'store/decryptImagesSlice';
import { getVideoByHashId } from 'store/decryptImagesSlice';
import { RootState, store } from 'store';
import { useSelector, useDispatch } from 'react-redux';

import axios from 'axios';

const useImageDecrypt = (aesKey: string, videoHashId: string, videoThumbnail?: string): any => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [hasDecrypted, setHasDecrypted] = useState<boolean>(false);

  const encryptedThumbnailsState = useSelector((state: RootState) => state.decryptedImages);
  const encryptedThumbnail = getVideoByHashId(encryptedThumbnailsState, videoHashId);

  const [decryptionKeys, setDecryptionKeys] = useState<any>(
    encryptedThumbnail?.decryptionKeys ?? {
      key: '',
      iv: ''
    }
  );

  const defaultImage = encryptedThumbnail && encryptedThumbnail.thumbnail ? encryptedThumbnail.thumbnail : '';
  const [decrytedVideoThumbnail, setDecrytedVideoThumbnail] = useState<string>(defaultImage);

  const handleDecryptVideoThumbanil = (imageUrl: string, isMounted: boolean) => {
    const { key, iv } = decryptionKeys;
    decryptImageFromUrl(imageUrl, key, iv).then((decryptedUrl) => {
      if (isMounted) {
        setDecrytedVideoThumbnail(decryptedUrl);
        setHasDecrypted(true);
        dispatch(
          addVideoThumbnail({
            videoHashId: videoHashId,
            thumbnail: decryptedUrl
          })
        );
      }
    });
  };

  useEffect(() => {
    let isMounted = true;
    const canDecrypt = decryptionKeys.key && videoThumbnail && !decrytedVideoThumbnail;
    const cloundFrontImage = videoThumbnail && videoThumbnail.includes('cloudfront.net');
    if (canDecrypt && cloundFrontImage && isMounted) {
      handleDecryptVideoThumbanil(videoThumbnail, isMounted);
    }

    return () => {
      isMounted = false;
      setHasDecrypted(false);
      setDecrytedVideoThumbnail(defaultImage);
    };
  }, [decryptionKeys?.key]);

  useEffect(() => {
    let isMounted = true;
    let isFetching = false;

    const fetchDecryptionKeys = async () => {
      try {
        setLoading(true);
        isFetching = true;
        const response = await api.get(`api/videos/${aesKey}/thumbnail/getkey/`);
        if (!isMounted) return; // Check if the component is still mounted
        if (!response.data.data) {
          setLoading(false);
          throw new Error('Decryption key not found in response data');
        }
        const { data, iv } = response.data;
        const decryptionKeys = await decryptAES_CBC(data, iv);
        if (!isMounted) return; // Check if the component is still mounted
        setDecryptionKeys(decryptionKeys);
        dispatch(setVideoDecrytionKey({ videoHashId: videoHashId, decryptionKeys: decryptionKeys }));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching decryption keys:', error);
      } finally {
        isFetching = false;
      }
    };

    if (aesKey && !encryptedThumbnail?.decryptionKeys && !isFetching) {
      fetchDecryptionKeys();
    } else {
      if (encryptedThumbnail && encryptedThumbnail.decryptionKeys) {
        setDecryptionKeys(encryptedThumbnail.decryptionKeys);
      }
    }

    return () => {
      isMounted = false;
      clearDecryptionKeys();
    };
  }, [aesKey, videoHashId]);

  const decryptAES_CBC = async (ciphertextBase64: string, ivHex: string): Promise<any> => {
    try {
      // Convert Base64 encoded ciphertext to Uint8Array
      const ciphertext = Uint8Array.from(atob(ciphertextBase64), (c) => c.charCodeAt(0));
      const key = hexToUint8Array(`${process.env.REACT_APP_THUMBNAIL_DECRYPTION_KEY}`);
      const iv = hexToUint8Array(ivHex);
      const importedKey = await window.crypto.subtle.importKey('raw', key, { name: 'AES-CBC' }, false, ['decrypt']);
      const decryptedData = await window.crypto.subtle.decrypt(
        {
          name: 'AES-CBC',
          iv: iv
        },
        importedKey,
        ciphertext
      );

      const decryptedText = new TextDecoder().decode(decryptedData);
      const jsonObject = JSON.parse(decryptedText);
      return jsonObject;
    } catch (error) {
      console.error('Decryption error:', error);
      throw error;
    }
  };

  const clearDecryptionKeys = () => {
    setDecryptionKeys({
      key: '',
      iv: ''
    });
  };

  return {
    loading,
    decryptionKeys,
    decrytedVideoThumbnail,
    hasDecrypted,
    clearDecryptionKeys
  };
};

const hexToUint8Array = (hexString: string): Uint8Array => {
  const matches = hexString.match(/.{1,2}/g);
  if (!matches) {
    throw new Error('Invalid hex string');
  }
  return new Uint8Array(matches.map((byte) => parseInt(byte, 16)));
};
const imageCache: { [key: string]: string } = {};
const requestLock: { [key: string]: boolean } = {};

export const decryptImageFromUrl = (imageUrl: string, decryptionKey: string, decryptionIv: string): Promise<string> => {
  // Generate a unique cache key based on imageUrl, decryptionKey, and decryptionIv
  const cacheKey = `${imageUrl}-${decryptionKey}-${decryptionIv}`;

  // Check if the URL is already cached
  if (imageCache[cacheKey]) {
    return Promise.resolve(imageCache[cacheKey]); // Return the cached URL
  }

  // Check if a request is already in progress for this combination
  if (requestLock[cacheKey]) {
    // If a request is already in progress, return a Promise that resolves when the image is cached
    return new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (imageCache[cacheKey]) {
          clearInterval(intervalId);
          resolve(imageCache[cacheKey]);
        }
      }, 100); // Check every 100 milliseconds if the image is cached
    });
  }

  // Lock the request for this combination
  requestLock[cacheKey] = true;

  // If the URL is not cached, proceed with decryption
  return new Promise((resolve, reject) => {
    axios
      .get(imageUrl, { responseType: 'blob' }) // Make sure we get the image as a blob
      .then((response: any) => {
        const key = hexToUint8Array(decryptionKey);
        const iv = hexToUint8Array(decryptionIv);

        const reader = new FileReader();

        reader.onloadend = () => {
          // Convert the result (base64 string) to Uint8Array
          const base64Image = reader.result?.toString().split(',')[1]; // Get only the base64 part
          if (!base64Image) {
            reject(new Error('Failed to read base64 image'));
            return;
          }

          // Convert base64 string to Uint8Array for decryption
          const encryptedData = Uint8Array.from(atob(base64Image), (c) => c.charCodeAt(0));
          // Decrypt the image using AES-CBC
          window.crypto.subtle
            .importKey('raw', key, { name: 'AES-CBC' }, false, ['decrypt'])
            .then((importedKey) => {
              window.crypto.subtle
                .decrypt(
                  {
                    name: 'AES-CBC',
                    iv: iv
                  },
                  importedKey,
                  encryptedData
                )
                .then((decryptedData) => {
                  // Create a blob from decrypted data and convert to image URL
                  const decryptedBlob = new Blob([decryptedData], { type: 'image/jpeg' });
                  const decryptedImageUrl = URL.createObjectURL(decryptedBlob);

                  // Cache the decrypted URL for future requests
                  imageCache[cacheKey] = decryptedImageUrl;

                  // Release the lock for this combination
                  requestLock[cacheKey] = false;

                  resolve(decryptedImageUrl);
                })
                .catch((error) => {
                  console.error('Error decrypting image:', error);
                  reject(error);
                });
            })
            .catch((error) => {
              console.error('Error importing key:', error);
              reject(error);
            });
        };

        // Convert the blob to base64 using FileReader
        reader.readAsDataURL(response.data);
      })
      .catch((error) => {
        console.error('Error fetching image:', error);
        reject(error);
      });
  });
};

export const decryptAES_CBC = async (ciphertextBase64: string, ivHex: string, videoHashId?: string): Promise<any> => {
  try {
    // Convert Base64 encoded ciphertext to Uint8Array
    const ciphertext = Uint8Array.from(atob(ciphertextBase64), (c) => c.charCodeAt(0));
    const key = hexToUint8Array(`${process.env.REACT_APP_THUMBNAIL_DECRYPTION_KEY}`);
    const iv = hexToUint8Array(ivHex);
    const importedKey = await window.crypto.subtle.importKey('raw', key, { name: 'AES-CBC' }, false, ['decrypt']);
    const decryptedData = await window.crypto.subtle.decrypt(
      {
        name: 'AES-CBC',
        iv: iv
      },
      importedKey,
      ciphertext
    );

    const decryptedText = new TextDecoder().decode(decryptedData);
    const jsonObject = JSON.parse(decryptedText);
    if (videoHashId) {
      store.dispatch(setVideoDecrytionKey({ videoHashId: videoHashId, decryptionKeys: jsonObject }));
    }
    return jsonObject;
  } catch (error) {
    console.error('Decryption error:', error);
    throw error;
  }
};

export default useImageDecrypt;
